import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons/faInfoCircle';
import { faDesktop } from '@fortawesome/pro-solid-svg-icons/faDesktop';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { Button } from 'ui-components/components/button';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';

import Global from '../../../store/global-selectors';
import { UserPermissionService } from '../../../services/user-permission-service';
import { emptyArray } from 'ui-components/default-props/empty';

import ConnectedDevices from './connected-devices';
import { deleteWorkstation } from '../api/ss-connect';

import { WORKSTATION } from './constants';
import messages from './messages';
import styles from './style.module.less';
import { WorkstationPropType } from './prop-types';

const Workstation = ({
  workstation,
  openModalForDeviceType,
  shouldShowPrintersAndScales = false,
  currentUserWorkstationIds = emptyArray,
}) => {
  const { isOnline, description, workstationId, userId } = workstation;
  const userList = useSelector(Global.selectSellerUsers);
  const users = useMemo(() => userList.reduce((usrs, user) => ({ ...usrs, [user.userId]: user }), {}), [userList]);

  const openModalForWorkstation = useCallback(() => {
    openModalForDeviceType(workstation, WORKSTATION);
  }, [workstation, openModalForDeviceType]);

  const deactivateWorkstation = useCallback(() => {
    deleteWorkstation(workstationId);
  }, [workstationId]);

  const hideWorkstationHeaderBorder = !shouldShowPrintersAndScales;
  const userRoles = useSelector(Global.selectUseruserRoles);
  const hasAdminOrConfigPermissions =
    UserPermissionService.userHasConfigurationRole(userRoles) || UserPermissionService.userHasAdministrationRole(userRoles);

  return (
    <div>
      <div
        className={classnames(
          styles['workstation-header'],
          { [styles.offline]: !isOnline },
          { [styles['no-bottom-border']]: hideWorkstationHeaderBorder },
        )}
      >
        <span className={styles['workstation-name']}>
          <FontAwesomeIcon className={styles['workstation-icon']} icon={faDesktop} />
          {description}
          <Button className={styles['edit-workstation']} onClick={openModalForWorkstation} theme='unstyled'>
            <FormattedMessage {...messages.edit} />
            <FontAwesomeIcon icon={faEdit} />
          </Button>
        </span>
        <Tooltip>
          <span className={styles['checkbox-column-items']}>
            <FormattedMessage {...messages.disabled} />
            <FontAwesomeIcon className={styles['checkbox-info']} icon={faInfoCircle} />
          </span>
          <TooltipContent>
            <FormattedMessage {...messages.disabledInfoTooltip} />
          </TooltipContent>
        </Tooltip>
        <Tooltip>
          <span className={styles['checkbox-column-items']}>
            <FormattedMessage {...messages.shared} />
            <FontAwesomeIcon className={styles['checkbox-info']} icon={faInfoCircle} />
          </span>
          <TooltipContent>
            <FormattedMessage {...messages.disabledInfoTooltip} />
          </TooltipContent>
        </Tooltip>
      </div>
      {shouldShowPrintersAndScales && (
        <ConnectedDevices
          currentUserWorkstationIds={currentUserWorkstationIds}
          hasAdminOrConfigPermissions={hasAdminOrConfigPermissions}
          openModal={openModalForDeviceType}
          userId={userId}
          workstationId={workstationId}
        />
      )}
      <div className={styles['workstation-info']}>
        <FormattedMessage {...messages.activatedBy} values={{ userName: users[userId]?.name }} />
        {hasAdminOrConfigPermissions && (
          <Button className={styles.deactivate} onClick={deactivateWorkstation} theme='link'>
            <FormattedMessage {...messages.deactivate} />
          </Button>
        )}
      </div>
    </div>
  );
};

Workstation.propTypes = {
  workstation: WorkstationPropType.isRequired,
  openModalForDeviceType: PropTypes.func.isRequired,
  shouldShowPrintersAndScales: PropTypes.bool,
  currentUserWorkstationIds: PropTypes.arrayOf(PropTypes.string),
};

export default Workstation;
